"use client";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "./context/AuthContext";
import { api } from "../lib/api";
import { PhoneInput } from "./ui/phone-input";
import { API_BASE_URL } from "../config/api.config";

const API = API_BASE_URL;

// --- UI atoms
function Avatar({ src, name }) {
  if (src)
    return (
      <img
        src={src}
        alt={name}
        className="h-16 w-16 rounded-full object-cover shadow-lg ring-4 ring-white"
      />
    );
  const initials = (name || "?")
    .split(" ")
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
  return (
    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-white grid place-content-center text-lg font-bold shadow-lg ring-4 ring-white">
      {initials}
    </div>
  );
}

function Chip({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-all transform hover:scale-105
        ${
          active
            ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white border-emerald-600 shadow-lg shadow-emerald-500/30"
            : "bg-white text-gray-700 border-gray-200 hover:border-emerald-300 hover:shadow-md"
        }`}>
      {children}
    </button>
  );
}

function DayPill({ date, selected, onClick }) {
  // date is already a Date object from addDays()
  const d = date instanceof Date ? date : new Date(date);
  const day = d.toLocaleDateString(undefined, { weekday: "short" });
  const dom = d.getDate();
  return (
    <button
      onClick={onClick}
      className={`w-20 h-20 rounded-2xl border-2 grid place-content-center text-sm transition-all transform hover:scale-105
        ${
          selected
            ? "bg-gradient-to-br from-emerald-600 to-teal-600 text-white border-emerald-600 shadow-lg shadow-emerald-500/30"
            : "bg-white text-gray-800 border-gray-200 hover:border-emerald-300 hover:shadow-md"
        }`}
      title={d.toDateString()}>
      <div
        className={`text-xs font-medium ${
          selected ? "opacity-90" : "opacity-60"
        }`}>
        {day}
      </div>
      <div className="text-lg font-bold">{dom}</div>
    </button>
  );
}

// --- Date helpers
function startOfWeek(date = new Date()) {
  const d = new Date(date);
  const diff = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - diff);
  d.setHours(0, 0, 0, 0);
  return d;
}
function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}
function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function Confirm() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user, token, login: authLogin } = useAuth();

  // Check if user is authenticated
  const isAuthenticated = !!(user && token);

  // Temp user state
  const [tempUser, setTempUser] = useState(null);
  const [originalTempEmail, setOriginalTempEmail] = useState(""); // Store original email to detect changes
  
  useEffect(() => {
    try {
      const stored = localStorage.getItem("bookatherapy_tempUser");
      if (stored) {
        const parsed = JSON.parse(stored);
        setTempUser(parsed);
        setOriginalTempEmail(parsed.email || "");
      }
    } catch (e) {
      console.error("Failed to parse stored temp user:", e);
    }
  }, []);

  // Auth modal state — shown when guest clicks "Sign in"
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authEmail, setAuthEmail] = useState("");
  const [authOtp, setAuthOtp] = useState("");
  const [authStep, setAuthStep] = useState("email"); // "email" | "otp"
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  // Auto-fill email from temp user when it loads
  useEffect(() => {
    if (tempUser && tempUser.email && !authEmail) {
      setAuthEmail(tempUser.email);
    }
  }, [tempUser]);

  const handleSendOTP = async () => {
    if (!authEmail || !authEmail.includes('@')) {
      setAuthError("Please enter a valid email address");
      return;
    }

    try {
      setAuthLoading(true);
      setAuthError("");

      // Normalize email for comparison
      const normalizedEmail = authEmail.trim().toLowerCase();
      // Compare against current tempUser email (from localStorage)
      const currentTempEmail = tempUser?.email?.toLowerCase() || "";
      const emailChanged = tempUser && currentTempEmail && currentTempEmail !== normalizedEmail;

      // If temp user exists and email changed, update temp user email in DB and localStorage
      if (emailChanged && tempUser) {
        try {
          const updateResponse = await fetch(`${API}/auth/users/temp/${tempUser._id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ email: normalizedEmail }),
          });

          if (updateResponse.ok) {
            const updateData = await updateResponse.json();
            // Update temp user in localStorage
            const updatedTempUser = {
              ...tempUser,
              email: updateData.user.email,
            };
            localStorage.setItem("bookatherapy_tempUser", JSON.stringify(updatedTempUser));
            setTempUser(updatedTempUser);
            setOriginalTempEmail(updateData.user.email);
            console.log("✅ Updated temp user email in DB and localStorage");
          } else {
            const errorData = await updateResponse.json();
            throw new Error(errorData.message || "Failed to update email");
          }
        } catch (updateError) {
          console.error("Error updating temp user email:", updateError);
          setAuthError(updateError.message || "Failed to update email. Please try again.");
          setAuthLoading(false);
          return;
        }
      }

      // Send OTP to the email (either original or updated)
      const response = await fetch(`${API}/auth/client/request-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: normalizedEmail }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to send OTP");
      }

      setAuthStep("otp");
    } catch (error) {
      setAuthError(error.message || "Failed to send OTP. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!authOtp || authOtp.length !== 6) {
      setAuthError("Please enter the 6-digit code");
      return;
    }

    try {
      setAuthLoading(true);
      setAuthError("");

      const response = await fetch(`${API}/auth/client/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: authEmail, code: authOtp }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Invalid code");
      }

      const data = await response.json();

      // Update auth context - IMPORTANT: login expects (userData, token) not (token, userData)
      if (authLogin && data.token) {
        authLogin(data.user, data.token);
        
        // Sync persona from localStorage to DB after login
        const localPersona = localStorage.getItem("bookatherapy_persona");
        if (localPersona) {
          try {
            const parsedPersona = JSON.parse(localPersona);
            if (parsedPersona && typeof parsedPersona === "object") {
              // Check if persona is filled
              const hasPersona = parsedPersona.comfortVibe || 
                                 parsedPersona.idealEscape || 
                                 parsedPersona.resetRitual || 
                                 parsedPersona.therapistEnergy || 
                                 parsedPersona.desiredOutcome || 
                                 parsedPersona.moodColor;
              
              if (hasPersona) {
                // Sync to DB
                try {
                  const syncResponse = await fetch(`${API}/persona/me`, {
                    method: "PUT",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${data.token}`,
                    },
                    credentials: "include",
                    body: JSON.stringify(parsedPersona),
                  });
                  
                  if (syncResponse.ok) {
                    console.log("✅ Synced persona from localStorage to DB after login");
                    // Clear localStorage persona after successful sync
                    localStorage.removeItem("bookatherapy_persona");
                  }
                } catch (syncErr) {
                  console.error("Failed to sync persona to DB after login:", syncErr);
                  // Keep persona in localStorage if sync fails
                }
              }
            }
          } catch (parseErr) {
            console.error("Failed to parse localStorage persona:", parseErr);
            localStorage.removeItem("bookatherapy_persona");
          }
        }
      }

      // Clear temp user from localStorage after successful login
      if (tempUser) {
        localStorage.removeItem("bookatherapy_tempUser");
        setTempUser(null);
      }

      // Reset auth modal state (modal will close automatically when user/token update)
      setAuthEmail("");
      setAuthOtp("");
      setAuthStep("email");
    } catch (error) {
      setAuthError(error.message || "Invalid code. Please try again.");
    } finally {
      setAuthLoading(false);
    }
  };


  const selection = state?.selection; // { therapistId, fullName, photoUrl?, time? }
  const context = state?.context || {}; // { setting, dateISO?, timeExact?, persona? }

  // Extract referral code from navigation state or localStorage
  const [referralCode, setReferralCode] = useState(state?.referralCode || null);

  // Check for referral code in localStorage if not in navigation state
  useEffect(() => {
    if (!referralCode) {
      const stored = localStorage.getItem("bookatherapy_referralCode");
      if (stored) {
        console.log("🎟️ [Confirm] Referral code loaded from localStorage:", stored);
        setReferralCode(stored);
      }
    } else {
      console.log("🎟️ [Confirm] Referral code received from navigation:", referralCode);
    }
  }, [referralCode]);

  // Debug log
  useEffect(() => {
    if (referralCode) {
      console.log("🎟️ [Confirm] Referral code received:", referralCode);
    }
  }, [referralCode]);

  // guard: if direct hit without selection, bounce
  useEffect(() => {
    if (!selection?.therapistId) {
      navigate("/book", { replace: true });
    }
  }, [selection, navigate]);

  // therapist details (in case photo/modalities needed)
  const [therapist, setTherapist] = useState(null);
  useEffect(() => {
    if (!selection?.therapistId) return;
    (async () => {
      try {
        const res = await api.therapist(selection.therapistId);
        setTherapist(res?.therapist || res || null);
      } catch {
        /* ignore */
      }
    })();
  }, [selection?.therapistId]);

  // calendar state
  // Get today's date (constant for the session)
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [activeDateISO, setActiveDateISO] = useState(() => {
    // If context has a date and it's not in the past, use it
    if (context.dateISO) {
      const contextDate = new Date(context.dateISO + "T12:00:00");
      contextDate.setHours(0, 0, 0, 0);
      if (contextDate >= today) {
        return context.dateISO;
      }
    }
    return toISO(today);
  });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [userBookings, setUserBookings] = useState([]); // User's existing bookings
  const [time, setTime] = useState(selection?.time || context?.timeExact || "");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [error, setError] = useState("");
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [selectedSetting, setSelectedSetting] = useState(() => {
    // If therapist has multiple available settings, default to first one
    // Otherwise use the context setting
    if (
      selection?.availableSettings &&
      selection.availableSettings.length > 0
    ) {
      return selection.availableSettings[0];
    }
    return (
      context?.setting ||
      (Array.isArray(context?.setting) ? context.setting[0] : null)
    );
  });

  // Fetch email from auth context and user profile from backend
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (user?.email) {
        setClientEmail(user.email);

        try {
          setLoadingProfile(true);
          const profile = await api.user.getProfile(user.email);
          console.log("🔍 [Confirm] Profile:", profile);
          if (profile) {
            // Set name from profile or fallback to auth user name
            if (profile.fullName) {
              setClientName(profile.fullName);
            } else if (user?.fullName) {
              setClientName(user.fullName);
            }
            // Set phone from profile (handle both nested and flat structure)
            const phone = profile.phone || profile.user?.phone;
            if (phone) {
              setClientPhone(phone);
            }
          } else {
            // No profile found, use auth user name if available
            if (user?.fullName) {
              setClientName(user.fullName);
            }
          }
        } catch (err) {
          // No saved profile found, use auth user name
          if (user?.fullName) {
            setClientName(user.fullName);
          }
        } finally {
          setLoadingProfile(false);
        }
      } else {
        setLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, [user]);

  // Save user profile (only called when proceeding to payment)
  const saveUserProfile = async (name, phone) => {
    if (user?.email) {
      try {
        await api.user.saveProfile({
          email: user.email,
          fullName: name || clientName,
          phone: phone !== undefined ? phone : clientPhone,
        });
      } catch (err) {
        console.error("Failed to save user profile:", err);
      }
    }
  };

  // Handle name change (no auto-save)
  const handleNameChange = (e) => {
    setClientName(e.target.value);
  };

  // Handle phone change (no auto-save)
  const handlePhoneChange = (e) => {
    setClientPhone(e.target.value);
  };

  // Fetch user's existing bookings to filter out already booked slots
  useEffect(() => {
    const fetchUserBookings = async () => {
      if (!clientEmail) return;
      try {
        const appointments = await api.appointments.getMyAppointments();
        // Get all upcoming appointments (not cancelled or completed) for this therapist
        const activeBookings = (appointments?.upcoming || []).filter((apt) => {
          // Check status
          if (apt.status === "cancelled" || apt.status === "completed") {
            return false;
          }
          
          // Check if it's for the same therapist
          const therapistId = apt.therapist?._id || apt.therapist;
          const therapistIdStr = typeof therapistId === "string" 
            ? therapistId 
            : therapistId?.toString();
          const selectionIdStr = typeof selection?.therapistId === "string"
            ? selection.therapistId
            : selection?.therapistId?.toString();
          
          return therapistIdStr === selectionIdStr;
        });
        setUserBookings(activeBookings);
      } catch (error) {
        console.warn("Failed to fetch user bookings:", error);
        setUserBookings([]);
      }
    };

    if (clientEmail && selection?.therapistId) {
      fetchUserBookings();
    }
  }, [clientEmail, selection?.therapistId]);

  // fetch slots
  async function loadSlots(dateISO, setting) {
    try {
      setLoadingSlots(true);
      const data = await api.therapistSlots(
        selection.therapistId,
        dateISO,
        setting,
      );
      const arr = Array.isArray(data) ? data : data?.slots || [];
      setSlots(arr);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }

  useEffect(() => {
    if (activeDateISO && selection?.therapistId && selectedSetting) {
      loadSlots(activeDateISO, selectedSetting);
    }
  }, [activeDateISO, selection?.therapistId, selectedSetting]);

  // Navigate to payment page
  async function handleProceedToPayment() {
    setError("");
    if (!clientName || !clientEmail)
      return setError("Please enter your name and email.");
    if (!clientPhone || clientPhone.trim().length === 0)
      return setError("Please enter your phone number.");
    if (!activeDateISO || !time)
      return setError("Please choose a date & time.");

    // Ensure setting is available - use selectedSetting
    const settingToUse = selectedSetting;
    if (!settingToUse) {
      console.error(
        "Setting is missing. Selected:",
        selectedSetting,
        "Context:",
        context,
      );
      return setError("Please select a setting (Home, Mobile, or Clinic).");
    }

    // Save profile before proceeding
    await saveUserProfile(clientName, clientPhone);

    // Navigate to payment page with all booking data
    navigate("/book/payment", {
      state: {
        selection,
        context: {
          ...context,
          setting: settingToUse, // Use selected setting
          dateISO: activeDateISO,
        },
        bookingData: {
          clientName,
          clientEmail,
          dateISO: activeDateISO,
          time,
        },
        referralCode, // Pass referral code through navigation
      },
    });
  }

  // Auth modal overlay — shown on demand when guest clicks "Sign in"
  const authModal = showAuthModal && (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {authStep === "email" ? "Sign In to Book" : "Enter Verification Code"}
          </h2>
          <p className="text-gray-600 mb-6">
            {authStep === "email"
              ? "Sign in for a faster experience — your details will be pre-filled"
              : `We sent a 6-digit code to ${authEmail}`}
          </p>
        </div>

        {authError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {authError}
          </div>
        )}

        {authStep === "email" ? (
          <div className="space-y-4">
            <input
              type="email"
              placeholder="Enter your email"
              value={authEmail}
              onChange={(e) => setAuthEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendOTP()}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
              disabled={authLoading}
              autoFocus
            />
            <div className="flex flex-col gap-3">
              <button
                onClick={handleSendOTP}
                disabled={authLoading}
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold hover:from-emerald-700 hover:to-teal-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {authLoading ? "Sending..." : "Send Code"}
              </button>
              <button
                onClick={() => setShowAuthModal(false)}
                disabled={authLoading}
                className="w-full px-6 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors disabled:opacity-50"
              >
                Continue as Guest
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Enter 6-digit code"
              value={authOtp}
              onChange={(e) => setAuthOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => e.key === "Enter" && handleVerifyOTP()}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition text-center text-2xl tracking-widest"
              maxLength={6}
              disabled={authLoading}
              autoFocus
            />
            <div className="flex flex-col gap-3">
              <button
                onClick={handleVerifyOTP}
                disabled={authLoading || authOtp.length !== 6}
                className="w-full px-6 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold hover:from-emerald-700 hover:to-teal-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {authLoading ? "Verifying..." : "Verify & Continue"}
              </button>
              <button
                onClick={() => { setAuthStep("email"); setAuthOtp(""); setAuthError(""); }}
                disabled={authLoading}
                className="w-full px-6 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-colors disabled:opacity-50"
              >
                Back
              </button>
            </div>
            <button
              onClick={handleSendOTP}
              disabled={authLoading}
              className="w-full text-sm text-emerald-600 hover:text-emerald-700 font-medium disabled:opacity-50"
            >
              Resend Code
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );

  if (!selection?.therapistId) return null;
  const photoUrl = selection.photoUrl || therapist?.photoUrl;

  // Format date for display
  // Fix timezone issue: append T12:00:00 to parse as noon local time
  const selectedDate = new Date(activeDateISO + "T12:00:00");
  const dateDisplay = selectedDate.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  const progressSteps = [
    { label: "Matches", status: "done" },
    { label: "Review & Confirm", status: "current" },
    { label: "Payment", status: "upcoming" },
  ];

  const appointmentSummary = [
    { label: "Therapist", value: selection.fullName },
    { label: "Setting", value: selectedSetting || "Not selected" },
    { label: "Date", value: dateDisplay },
    { label: "Time", value: time || "Choose a time" },
  ];

  const yourDetails = [
    { label: "Name", value: clientName || "—" },
    { label: "Email", value: clientEmail || "—" },
    { label: "Phone", value: clientPhone || "—" },
  ];

  return (
    <div className="relative min-h-screen bg-slate-100 overflow-x-hidden">
      {/* Auth modal overlay — only shown when guest clicks "Sign in" */}
      {authModal}

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-x-[-10%] top-[-15%] h-[380px] bg-[radial-gradient(circle_at_center,_rgba(12,164,117,0.18),_rgba(148,191,255,0.08)_55%,_transparent_75%)] blur-3xl" />
        <div className="absolute left-[-15%] bottom-0 h-[360px] w-[360px] rounded-full bg-[radial-gradient(circle,_rgba(79,70,229,0.15),_transparent_65%)] blur-3xl" />
        <div className="absolute right-[-10%] top-1/3 h-[320px] w-[320px] rounded-full bg-[radial-gradient(circle,_rgba(6,182,212,0.18),_transparent_60%)] blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[1100px] flex-col px-4 pb-16 pt-10 sm:px-6 lg:px-8">
        <header className="mb-10 flex flex-wrap items-center justify-between gap-4 text-slate-700">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 rounded-full border border-slate-300/60 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] shadow-sm transition hover:border-emerald-300 hover:text-emerald-700">
            <span className="text-lg">←</span>
            Back to matches
          </button>
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.26em] text-emerald-600">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.7)]" />
            Confirm Booking
          </div>
        </header>

        {/* Guest sign-in prompt — only shown when not authenticated */}
        {!isAuthenticated && (
          <div className="mb-6 flex items-center justify-between gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 shrink-0 rounded-full bg-emerald-100 flex items-center justify-center">
                <svg className="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-emerald-900">Already have an account?</p>
                <p className="text-xs text-emerald-700">Sign in to pre-fill your details and track appointments.</p>
              </div>
            </div>
            <button
              onClick={() => { setAuthStep("email"); setAuthError(""); setShowAuthModal(true); }}
              className="shrink-0 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 transition"
            >
              Sign in
            </button>
          </div>
        )}

        <section className="mb-10 text-slate-800">
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
            Let’s lock in your session
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-slate-600">
            Review your therapist details, choose a time that works, and confirm
            your contact information. You’re only two clicks away from feeling
            better.
          </p>

          <div className="mt-6 flex flex-wrap gap-4">
            {progressSteps.map((step, idx) => (
              <div
                key={step.label}
                className={`group flex items-center gap-3 rounded-full border px-4 py-2 text-xs font-semibold transition ${
                  step.status === "done"
                    ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-700"
                    : step.status === "current"
                    ? "border-emerald-300 bg-white text-emerald-600 shadow"
                    : "border-slate-200 bg-white text-slate-300"
                }`}>
                <span
                  className={`grid h-6 w-6 place-content-center rounded-full text-[10px] ${
                    step.status === "done"
                      ? "bg-emerald-400 text-white"
                      : step.status === "current"
                      ? "bg-emerald-500 text-white"
                      : "border border-slate-200 text-slate-300"
                  }`}>
                  {idx + 1}
                </span>
                {step.label}
              </div>
            ))}
          </div>
        </section>

        <div className="grid flex-1 gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
          <div className="space-y-8">
            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 }}
              className="w-full rounded-3xl border border-white/70 bg-white p-6 shadow-[0_20px_60px_-40px_rgba(22,163,74,0.45)]">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                <Avatar src={photoUrl} name={selection.fullName} />
                <div className="flex-1 text-slate-800">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-xl font-semibold">
                      {selection.fullName}
                    </h2>
                    {selectedSetting && (
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-600">
                        {selectedSetting}
                      </span>
                    )}
                  </div>
                  {therapist?.bioSummary && (
                    <p className="mt-2 max-w-2xl text-sm text-slate-500">
                      {therapist.bioSummary}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    {therapist?.modalities?.slice(0, 3)?.map((modality) => (
                      <span
                        key={modality}
                        className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-emerald-700">
                        {modality}
                      </span>
                    ))}
                    {therapist?.languages?.length && (
                      <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-emerald-700">
                        Speaks {therapist.languages.slice(0, 2).join(", ")}
                      </span>
                    )}
                    {therapist?.experienceYears && (
                      <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-emerald-700">
                        {therapist.experienceYears}+ years experience
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.section>

            {/* Setting Selection */}
            {selection?.availableSettings &&
              selection.availableSettings.length > 1 && (
                <motion.section
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.09 }}
                  className="w-full rounded-3xl border border-white/70 bg-white p-6 shadow-[0_20px_60px_-40px_rgba(59,130,246,0.35)]">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
                      Setting
                    </p>
                    <h3 className="mt-1 text-lg font-semibold text-slate-800">
                      Choose where you'd like your massage
                    </h3>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {selection.availableSettings.map((setting) => {
                      const settingIcons = {
                        Home: "🏠",
                        Mobile: "🚗",
                        Clinic: "🏥",
                      };
                      const settingDescriptions = {
                        Home: "Therapist comes to your home",
                        Mobile: "Therapist brings portable table",
                        Clinic: "Visit therapist's clinic",
                      };
                      return (
                        <button
                          key={setting}
                          type="button"
                          onClick={() => {
                            setSelectedSetting(setting);
                            setTime(""); // Reset time when setting changes
                          }}
                          className={`flex-1 min-w-[140px] sm:min-w-[180px] rounded-2xl border-2 p-4 transition-all transform hover:scale-105 ${
                            selectedSetting === setting
                              ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white border-blue-600 shadow-lg shadow-blue-500/30"
                              : "bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:shadow-md"
                          }`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">
                              {settingIcons[setting]}
                            </span>
                            <span className="font-semibold text-lg">
                              {setting}
                            </span>
                          </div>
                          <p
                            className={`text-xs ${
                              selectedSetting === setting
                                ? "text-blue-50"
                                : "text-gray-500"
                            }`}>
                            {settingDescriptions[setting]}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </motion.section>
              )}

            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.12 }}
              className="w-full rounded-3xl border border-white/70 bg-white p-6 shadow-[0_24px_65px_-48px_rgba(59,130,246,0.35)]">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">
                    Availability
                  </p>
                  <h3 className="mt-1 text-lg font-semibold text-slate-800">
                    Pick a date and a time that suits you
                  </h3>
                </div>
              </div>

              <div className="mt-6 pb-2">
                <div className="flex flex-wrap  gap-3">
                  {Array.from({ length: 7 }).map((_, i) => {
                    // Show 7 dates starting from today, up to today+6
                    const d = addDays(today, i);
                    const iso = toISO(d);
                    return (
                      <DayPill
                        key={iso}
                        date={d}
                        selected={activeDateISO === iso}
                        onClick={() => setActiveDateISO(iso)}
                      />
                    );
                  })}
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/90 p-5">
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <span className="text-xl">📅</span>
                  <div>
                    <p className="font-semibold text-slate-800">
                      Selected date
                    </p>
                    <p className="text-slate-600">{dateDisplay}</p>
                  </div>
                </div>

                <div className="mt-4">
                  <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <span className="text-base">⏰</span> Available times
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {loadingSlots ? (
                      <>
                        <div className="h-10 w-28 animate-pulse rounded-full bg-slate-200/60" />
                        <div className="h-10 w-28 animate-pulse rounded-full bg-slate-200/60" />
                        <div className="h-10 w-28 animate-pulse rounded-full bg-slate-200/60" />
                      </>
                    ) : (
                      (() => {
                        const now = new Date();
                        const isToday = toISO(today) === activeDateISO;

                        // Filter out past times and already booked slots
                        const availableSlots = slots.filter((slotTime) => {
                          const [hours, minutes] = slotTime
                            .split(":")
                            .map(Number);

                          // Create a datetime for the selected date and slot time
                          const slotDateTime = new Date(
                            activeDateISO + "T12:00:00",
                          );
                          slotDateTime.setHours(hours, minutes, 0, 0);

                          // Only show slots that are in the future
                          if (slotDateTime <= now) {
                            return false;
                          }

                          // Filter out slots that the user has already booked
                          const isAlreadyBooked = userBookings.some((booking) => {
                            if (!booking.appointmentDate) return false;
                            
                            // Parse booking date (could be ISO string or Date object)
                            const bookingDate = new Date(booking.appointmentDate);
                            if (isNaN(bookingDate.getTime())) return false;
                            
                            // Get booking date in local timezone (YYYY-MM-DD format)
                            const bookingDateISO = toISO(bookingDate);
                            
                            // Check if it's the same date
                            if (bookingDateISO !== activeDateISO) return false;
                            
                            // Extract time from booking date (in local timezone)
                            const bookingHours = bookingDate.getHours();
                            const bookingMinutes = bookingDate.getMinutes();
                            const bookingTimeStr = `${String(bookingHours).padStart(2, "0")}:${String(bookingMinutes).padStart(2, "0")}`;
                            
                            // If the slot time matches the booking start time, it's already booked
                            if (bookingTimeStr === slotTime) {
                              return true;
                            }
                            
                            // Also check if slot falls within the booking duration
                            const bookingDuration = booking.duration || booking.durationMins || 60;
                            const slotMinutes = hours * 60 + minutes;
                            const bookingStartMinutes = bookingHours * 60 + bookingMinutes;
                            const bookingEndMinutes = bookingStartMinutes + bookingDuration;
                            
                            // Check if slot starts during the booking period
                            return slotMinutes >= bookingStartMinutes && slotMinutes < bookingEndMinutes;
                          });

                          return !isAlreadyBooked;
                        });

                        return availableSlots.length ? (
                          availableSlots.map((slot) => (
                            <Chip
                              key={slot}
                              active={time === slot}
                              onClick={() => setTime(slot)}>
                              {slot}
                            </Chip>
                          ))
                        ) : (
                          <span className="rounded-full border border-amber-300/40 bg-amber-100/80 px-4 py-2 text-sm text-amber-600">
                            No times available on this day — pick another date.
                          </span>
                        );
                      })()
                    )}
                  </div>
                </div>

                <div className="mt-6 grid gap-3 sm:grid-cols-[180px_1fr]">
                  <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Need a different day?
                  </label>
                  <input
                    type="date"
                    value={activeDateISO}
                    onChange={(e) => setActiveDateISO(e.target.value)}
                    min={toISO(new Date())}
                    className="h-12 rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-700 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-300/40"
                  />
                </div>
              </div>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.18 }}
              className="w-full rounded-3xl border border-white/70 bg-white p-6 shadow-[0_24px_60px_-48px_rgba(56,189,248,0.25)]">
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 text-lg text-emerald-500">
                  👤
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">
                    Your information
                  </h3>
                  <p className="text-sm text-slate-500">
                    We’ll send confirmation and reminders here.
                  </p>
                </div>
              </div>

              {loadingProfile ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="h-12 animate-pulse rounded-2xl bg-slate-200/60" />
                  <div className="h-12 animate-pulse rounded-2xl bg-slate-200/60" />
                  <div className="h-12 animate-pulse rounded-2xl bg-slate-200/60 sm:col-span-2" />
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Full name *
                    </label>
                    <input
                      type="text"
                      value={clientName}
                      onChange={handleNameChange}
                      className="h-12 rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-800 placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-300/40"
                      placeholder="Alex Morgan"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      placeholder="your@email.com"
                      className={`h-12 rounded-2xl border px-4 text-sm placeholder:text-slate-400 ${
                        isAuthenticated
                          ? "border-slate-200 bg-slate-100 text-slate-500 cursor-not-allowed"
                          : "border-slate-300 bg-white text-slate-900 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      }`}
                      disabled={isAuthenticated}
                    />
                  </div>

                  <div className="flex flex-col gap-2 sm:col-span-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Phone number *
                    </label>
                    <PhoneInput
                      value={clientPhone}
                      onChange={handlePhoneChange}
                      defaultCountry="CA"
                      className="rounded-2xl border border-slate-200 bg-slate-50 text-slate-800 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-300/40"
                    />
                  </div>
                </div>
              )}

              {user?.email && (
                <div className="mt-4 flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-700">
                  <span className="text-base">✓</span>
                  <span>We’ll remember your details for next time.</span>
                </div>
              )}
            </motion.section>
          </div>

          <motion.aside
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.24 }}
            className="space-y-6 min-w-0 w-full ">
            <div className="space-y-6 lg:sticky lg:top-10">
              <section className="w-full rounded-3xl border border-white/70 bg-white p-6 text-slate-800 shadow-[0_20px_65px_-35px_rgba(34,211,238,0.35)]">
                <h3 className="text-lg font-semibold">Booking summary</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Double-check the details below before continuing to payment.
                </p>

                <dl className="mt-6 space-y-4 text-sm">
                  {appointmentSummary.map(({ label, value }) => (
                    <div
                      key={label}
                      className="flex items-center justify-between gap-4 border-b border-slate-200 pb-3 last:border-0 last:pb-0">
                      <dt className="text-slate-500">{label}</dt>
                      <dd
                        className={`max-w-[60%] text-right font-medium ${
                          value === "Choose a time"
                            ? "text-amber-600"
                            : "text-slate-800"
                        }`}>
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Your details
                  </p>
                  <ul className="mt-3 space-y-2 text-sm w-full">
                    {yourDetails.map(({ label, value }) => (
                      <li
                        key={label}
                        className="flex items-start justify-between gap-4">
                        <span className="text-slate-500">{label}</span>
                        <span className=" text-right font-medium text-slate-800">
                          {value}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {error && (
                  <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-100 px-4 py-3 text-xs text-amber-700">
                    {error}
                  </div>
                )}

                <div className="mt-6 flex flex-col gap-3">
                  <button
                    className="h-12 rounded-2xl bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:scale-[1.01] hover:shadow-emerald-500/40 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={handleProceedToPayment}
                    disabled={
                      !clientName ||
                      !clientEmail ||
                      !clientPhone ||
                      !time ||
                      loadingProfile
                    }>
                    {loadingProfile
                      ? "Preparing your booking…"
                      : "Continue to payment"}
                  </button>
                  <button
                    className="h-11 rounded-2xl border border-slate-200 bg-white text-sm font-medium text-slate-600 transition hover:border-emerald-300 hover:text-emerald-600"
                    onClick={() => navigate(-1)}>
                    Adjust matches
                  </button>
                </div>

                <p className="mt-4 text-center text-[11px] text-slate-500">
                  Secure checkout • We’ll hold your spot for the next 10
                  minutes.
                </p>
              </section>

              <section className="w-full rounded-3xl border border-white/70 bg-white p-5 text-xs text-slate-600">
                <h4 className="text-sm font-semibold text-slate-800">
                  Need a hand?
                </h4>
                <p className="mt-2 leading-relaxed">
                  Have a question before you confirm? Our team is available by
                  chat or email 7 days a week. We’re here to make sure you feel
                  confident about your booking.
                </p>
              </section>
            </div>
          </motion.aside>
        </div>
      </div>
    </div>
  );
}
